import { subDays, startOfDay, endOfDay, format as formatDate, differenceInDays } from 'date-fns';
import Dygraph from 'dygraphs/index.es5';
import template from './barGraphPanel.component.html';

/* @ngInject */
function barGraphPanelController($scope, $timeout, $filter, $element, growl,
                                 EquipmentInfoService, DashboardService, BarGraphPanelService, $window) {
  const vm = this;
  vm.$onInit = init;
  vm.$postLink = postLink;
  vm.$onDestroy = destroy;
  vm.refresh = refresh;
  vm.setActiveEquipment = setActiveEquipment;
  vm.toggleStartDatePicker = toggleStartDatePicker;
  vm.toggleEndDatePicker = toggleEndDatePicker;

  let growlMessage;
  const RESIZE_TIMEOUT = 300;
  const RESIZE_DEBOUNCE = 500;
  const INIT_START_DAYS_SUB = 7;

  const X_AXIS_BREAKPOINTS = {
    0: {
      0: 'TWO_DAILY',
      325: 'DAILY'
    },
    14: {
      0: 'WEEKLY',
      325: 'TWO_DAILY',
      500: 'DAILY'
    },
    31: {
      0: 'WEEKLY',
      500: 'TWO_DAILY',
      1000: 'DAILY'
    },
    61: {
      0: 'MONTHLY',
      325: 'WEEKLY',
      700: 'TWO_DAILY',
      1500: 'DAILY'
    },
    92: {
      0: 'MONTHLY',
      500: 'WEEKLY',
      1500: 'TWO_DAILY',
      2500: 'DAILY'
    },
    183: {
      0: 'QUARTERLY',
      325: 'MONTHLY',
      1200: 'WEEKLY',
      2500: 'TWO_DAILY',
      5000: 'DAILY'
    }
  };

  vm.windowSize = $window.innerWidth;
  let stopResizeDeReg;
  vm.headerMenu = [{ text: 'Export Data', do: exportData }];
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.activeEquipmentId = null;
  vm.equipment = {};
  vm.startDate = {
    date: subDays(startOfDay(new Date()), INIT_START_DAYS_SUB),
    opened: false
  };
  vm.endDate = {
    date: endOfDay(new Date()),
    opened: false
  };
  /** User has pressed the plot button and a refresh is in progress */
  vm.refreshingData = false;


  const datepickerOptions = {
    showWeeks: false
  };

  vm.datepickerStartOptions = _.merge({
    maxDate: vm.endDate.date
  }, datepickerOptions);

  vm.datepickerEndOptions = _.merge({
    minDate: vm.startDate.date
  }, datepickerOptions);

  async function init() {
    // check panel configuration state
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];
    vm.activeEquipmentId = vm.panel.equipment[0];
  }

  async function postLink() {
    if (_.isEmpty(vm.panel.equipment)) {
      vm.editPanel.needsToBeConfigured = true;
      vm.stopSpinner = true;
    } else {
      vm.editPanel.needsToBeConfigured = false;
      vm.equipment = _.keyBy(await EquipmentInfoService.getFullEquipment(vm.panel.equipment), 'id');
      vm.stopSpinner = true;
      setOptions();
      const data = await getData();
      createGraph(data);
      $scope.$on('panel-resized', resize);
      stopResizeDeReg = angular.element($window).on('resize', _.debounce(() => {
        vm.windowSize = $window.innerWidth;
        resize();
      }, RESIZE_DEBOUNCE));
    }
  }

  function destroy() {
    if (_.isFunction(stopResizeDeReg)) {
      stopResizeDeReg();
      stopResizeDeReg = undefined;
    }
  }

  async function getData() {
    growlMessage?.destroy();
    let data;
    try {
      data = await BarGraphPanelService.getData(
        vm.activeEquipmentId,
        vm.startDate.date,
        vm.endDate.date,
        vm.selectedValue,
        _.get(vm, ['panel', 'config', vm.activeEquipmentId, 'listenForEvent'], 'sensors/regularUpdate')
      );
      if (vm.refreshingData && _.isEmpty(data)) {
        // Message only shown when getData is called by UX
        growlMessage = growl.warning('No events returned');
      }
    } catch (error) {
      growlMessage = growl.error(`Failed to get events: ${_.get(error, 'data.error')}`);
    }
    return data.length > 0 ?
      data :
      [[0, 0]] // clear graph hack
    ;
  }

  async function refresh() {
    vm.refreshingData = true;
    const data = await getData();
    vm.graph.updateOptions({ file: data, dateWindow: [vm.startDate.date, vm.endDate.date] });
    vm.refreshingData = false;
  }

  function setActiveEquipment({ id }) {
    vm.activeEquipmentId = id;
    setOptions();
    refresh();
  }

  function toggleStartDatePicker() {
    vm.startDate.opened = !vm.startDate.opened;
    vm.datepickerStartOptions.maxDate = vm.endDate.date; // prevents selecting a date before end date
  }

  function toggleEndDatePicker() {
    vm.endDate.opened = !vm.endDate.opened;
    vm.datepickerEndOptions.minDate = vm.startDate.date;
  }

  function setOptions() {
    vm.options = getOptions();
    vm.selectedValue = _.has(vm.options, vm.selectedValue) ? vm.selectedValue : _.findKey(vm.options); // use first key
  }

  function getOptions(id = vm.activeEquipmentId) {
    const { points = {}, pointPrettyNames = {}, calculate = {} } = _.get(vm.equipment, [id, 'settings'], {});
    const allPoints = { ...points, ...calculate };

    const { includeInGraph = [], excludeFromGraph = [] } = _.get(vm.panel, ['config', id], {});

    const options = {};

    // includeInGraph overrides excludeFromGraph
    const keys = includeInGraph.length ?
      includeInGraph :
      excludeFromGraph.length ?
        _.difference(_.keys(allPoints), excludeFromGraph) :
        _.keys(allPoints);

    _.forEach(_.sortBy(keys), (key) => {
      if (!_.isNil(allPoints[key])) {
        options[key] = pointPrettyNames[key] || key;
      }
    });

    return options;
  }

  function resize() {
    $timeout(() => {
      vm.graph.resize();
    }, RESIZE_TIMEOUT);
  }

  function createGraph(data) {
    vm.graph = new Dygraph(
      $element.find('#bar-graph-panel')[0],
      data, {
        includeZero: true,
        plotter: barGraphPlotter,
        showRangeSelector: false,
        rightGap: 5,
        animatedZooms: true,
        hideOverlayOnMouseOut: false,
        dateWindow: [vm.startDate.date, vm.endDate.date],
        interactionModel: {},
        color: ['#046bb5'],
        axes: {
          x: {
            axisLabelFormatter: date => formatDate(new Date(date), 'MM/dd/yy'),
            drawGrid: false,
            ticker: (min, max, curPixApart, opts, dygraph, vals) =>
              determineDateTicker(min, max, curPixApart, opts, dygraph, vals, vm.windowSize, X_AXIS_BREAKPOINTS)
          },
          y: {
            axisLabelFormatter: _customAxisValues,
            valueFormatter: _customAxisValues,
            axisLabelWidth: 65,
            pixelsPerLabel: 35
          }
        },
        xAxisHeight: 62,
        legendFormatter: (event) => {
          if (event?.x === 0) { // date is 0 milliseconds
            return 'No Data';
          } else if (!event?.x) {
            return '';
          }
          return `${formatDate(new Date(event.x), 'MM/dd/yy')}: <b>${_.get(event, ['series', 0, 'yHTML'])}</b>`;
        }
      });
  }

  function determineDateTicker(min, max, curPixApart, opts, dygraph, vals, windowSize, breakpoints) {
    const dateRange = differenceInDays(max, min);
    const leastDate = _.reduce(breakpoints, (currentDate, _ignore, nextDate) => {
      const nextDateNum = Number(nextDate);
      return dateRange >= nextDateNum ? nextDateNum : currentDate;
    }, 0);

    const leastWidth = _.reduce(breakpoints[leastDate],
      (curWidth, _ignore, nextWidth) => {
        const nextWidthNum = Number(nextWidth);
        return windowSize >= nextWidthNum ? nextWidthNum : curWidth;
      }, 0);

    const frequency = _.get(breakpoints, [leastDate, leastWidth], null);
    if (frequency) {
      return Dygraph.getDateAxis(min, max, Dygraph.Granularity[frequency], opts, dygraph, vals); // use custom ticks
    }
    return Dygraph.dateTicker(min, max, curPixApart, opts, dygraph, vals); // use default
  }

  function _customAxisValues(val) {
    const { toFixed, units } =
    _.get(vm.panel, ['config', vm.activeEquipmentId, 'customAxisValues', vm.selectedValue], {});

    val = $filter('number')(val, _.isInteger(val) ? 0 : toFixed);

    if (units) {
      val = `${val} ${units}`;
    }

    return val;
  }

  function barGraphPlotter(event) {
    const ctx = event.drawingContext;
    const points = event.points;
    const yBottom = event.dygraph.toDomYCoord(0);

    ctx.fillStyle = event.color;
    // Find the minimum separation between x-values.
    // This determines the bar width.
    let minSep = ctx.canvas.width / 2;
    if (points.length === 1) {
      minSep = points[0].canvasx > minSep ? ctx.canvas.width - points[0].canvasx : points[0].canvasx;
    } else {
      for (let i = 1; i < points.length; i++) {
        const sep = points[i].canvasx - points[i - 1].canvasx;
        if (sep < minSep) {
          minSep = sep;
        }
      }
    }
    const barWidth = Math.floor(2 / 3 * minSep);
    // Do the actual plotting.
    for (const point of points) {
      const centerX = point.canvasx;

      ctx.fillRect(centerX - (barWidth / 2), point.canvasy,
        barWidth, yBottom - point.canvasy);

      ctx.strokeRect(centerX - (barWidth / 2), point.canvasy,
        barWidth, yBottom - point.canvasy);
    }
  }

  async function exportData() {
    const allPointPrettyNames = {};
    const equipmentValues = _.map(vm.equipment, (eq, id) => {
      const pointMappings = getOptions(id);
      _.set(allPointPrettyNames, [id], pointMappings);
      return {
        id,
        name: eq.name,
        values: _.keys(pointMappings),
        eventName: _.get(vm, ['panel', 'config', id, 'listenForEvent'], 'sensors/regularUpdate')
      };
    });

    try {
      const { startDate: start, endDate: end } = vm;
      const blob = new Blob(
        [await BarGraphPanelService.exportData(equipmentValues, start.date, end.date, allPointPrettyNames)],
        { type: 'data:text/csv;charset=utf-8' }
      );
      const dateRange = `${$filter('date')(start.date, 'mediumDate')}-${$filter('date')(end.date, 'mediumDate')}`;
      saveAs(blob, `Bar Graph Export ${dateRange}.csv`);
    } catch (err) {
      if (growlMessage) {
        growlMessage.destroy();
        growlMessage = null;
      }
      console.error(err);
      growl.error(`Export Failed: ${err}`);
    }
  }

}

export default {
  name: 'barGraphPanel',
  template,
  controller: barGraphPanelController,
  bindings: {
    panelId: '<'
  }
};
